import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';
import { NumberMaskInput, CurrencyMaskInput } from '@elements/CustomMaskInput';
import { useEffect, useMemo } from 'react';
import { CommonUtility } from '@utility/common';
import { BoldText, PageHeaderText } from '@elements/Common';
import {
  AppTable,
  AppTableCell,
  AppTableBody,
  AppTableRoot,
  AppTableRow,
} from '@components/AppTable';
import { Button, TextField } from '@skupreme/shared/ui';
import { FormItem } from '@skupreme/shared/ui/form';
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import type { ITableColumn } from 'types/common/table';

export interface ICustomLineItem {
  item_name: string;
  qty: number | null;
  price: number | null;
  [key: string]: any;
}

/* The masks hand back strings and saved items hold numbers; either may be empty. */
const amount = z.union([z.string(), z.number()]).nullable();

const lineItemSchema = z.object({
  item_name: z
    .string()
    .refine(value => value.trim().length > 0, 'Item Name is required'),
  qty: amount.refine(value => !!value, 'Quantity is required'),
  price: amount.refine(value => !!value, 'Unit Cost is required'),
});

type LineItemForm = z.infer<typeof lineItemSchema>;

const emptyLineItem: LineItemForm = { item_name: '', qty: null, price: null };

interface IAddUpdateLineItemProps {
  showForm: boolean;
  setShowForm: (value: boolean) => void;
  item: ICustomLineItem | null;
  isEditMode: boolean;
  onSave: (item: ICustomLineItem, isEditMode: boolean) => void;
  cancel: () => void;
  currency: string;
}

interface IItemRowProps {
  item?: ICustomLineItem;
  isEditMode: boolean;
  removeItem: (item: ICustomLineItem) => void;
  updateItem: (item: ICustomLineItem) => void;
  currency: string;
}

interface ICustomLineItemsProps {
  items: ICustomLineItem[];
  isEditMode: boolean;
  removeItem: (item: ICustomLineItem) => void;
  loading: boolean;
  updateItem: (item: ICustomLineItem) => void;
  currency: string;
}

export function AddUpdateLineItem({
  showForm,
  setShowForm,
  item,
  isEditMode,
  onSave,
  cancel,
  currency,
}: IAddUpdateLineItemProps) {
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<LineItemForm>({
    resolver: zodResolver(lineItemSchema),
    defaultValues: emptyLineItem,
  });
  const [qty, price] = useWatch({ control, name: ['qty', 'price'] });

  // biome-ignore lint/correctness/useExhaustiveDependencies: loads the form only when another item is picked for editing, as before
  useEffect(() => {
    if (item) {
      reset({ ...emptyLineItem, ...item });
      setShowForm(true);
    }
  }, [item]);

  const cancelUpdate = () => {
    reset(emptyLineItem);
    setShowForm(false);
    cancel();
  };

  const save = (values: LineItemForm) => {
    /* The masks' strings go out unconverted, as they always have. */
    onSave(
      {
        ...(item || {}),
        ...values,
      } as ICustomLineItem,
      isEditMode,
    );
    cancelUpdate();
  };

  if (!showForm) {
    return null;
  }

  return (
    <div className="sk:my-4">
      <PageHeaderText>Additional Items</PageHeaderText>
      <AppTableRoot className="sk:mt-2">
        <AppTableBody>
          <AppTableRow verticalAlign="middle">
            <AppTableCell>
              <BoldText>
                {isEditMode ? 'Update a Custom Item' : 'Add a Custom Item'}
              </BoldText>
            </AppTableCell>
            <AppTableCell width={4}>
              <Controller
                control={control}
                name="item_name"
                render={({ field }) => (
                  <FormItem
                    label="Item Name"
                    isRequired
                    error={errors.item_name?.message}
                  >
                    {({ labelId, errorId }) => (
                      <TextField
                        aria-labelledby={labelId}
                        aria-describedby={errorId}
                        placeholder="Item Name"
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        isInvalid={!!errors.item_name}
                        className="sk:w-full"
                      />
                    )}
                  </FormItem>
                )}
              />
            </AppTableCell>
            <AppTableCell width={2}>
              <Controller
                control={control}
                name="qty"
                render={({ field }) => (
                  <FormItem label="Quantity" error={errors.qty?.message}>
                    {({ labelId }) => (
                      <NumberMaskInput
                        aria-labelledby={labelId}
                        placeholder="Quantity"
                        value={field.value}
                        onChange={field.onChange}
                      />
                    )}
                  </FormItem>
                )}
              />
            </AppTableCell>
            <AppTableCell width={2}>
              <Controller
                control={control}
                name="price"
                render={({ field }) => (
                  <FormItem label="Unit Cost" error={errors.price?.message}>
                    {({ labelId }) => (
                      <CurrencyMaskInput
                        aria-labelledby={labelId}
                        placeholder="Unit Cost"
                        value={field.value || ''}
                        onChange={field.onChange}
                        currencyCode={currency}
                      />
                    )}
                  </FormItem>
                )}
              />
            </AppTableCell>
            <AppTableCell textAlign="right">
              Total{' '}
              <BoldText>
                {CommonUtility.currencyFormat(
                  Number(qty || 0) * Number(price || 0),
                  currency,
                )}
              </BoldText>
            </AppTableCell>
            <AppTableCell textAlign="right">
              <div className="sk:flex sk:justify-end sk:gap-2">
                <Button variant="primary" onPress={() => handleSubmit(save)()}>
                  {isEditMode ? (
                    'Update'
                  ) : (
                    <>
                      <PlusIcon aria-hidden />
                      Item
                    </>
                  )}
                </Button>
                {isEditMode && <Button onPress={cancelUpdate}>Cancel</Button>}
              </div>
            </AppTableCell>
          </AppTableRow>
        </AppTableBody>
      </AppTableRoot>
    </div>
  );
}

function ItemRow({
  item,
  isEditMode,
  removeItem,
  updateItem,
  currency,
}: IItemRowProps) {
  if (!item) return null;

  return (
    <AppTableRow>
      <AppTableCell>
        <div className="sk:flex sk:items-center sk:gap-1">
          {item.item_name}
          {isEditMode && (
            <>
              <Button
                variant="ghost"
                size="sm"
                shape="circle"
                aria-label="Edit item"
                onPress={() => updateItem(item)}
              >
                <PencilSimpleIcon aria-hidden />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                shape="circle"
                aria-label="Remove item"
                onPress={() => removeItem(item)}
              >
                <TrashIcon aria-hidden />
              </Button>
            </>
          )}
        </div>
      </AppTableCell>
      <AppTableCell>{CommonUtility.numberWithCommas(item.qty)}</AppTableCell>
      <AppTableCell>
        {CommonUtility.currencyFormat(item.price, currency)}
      </AppTableCell>
      <AppTableCell>
        {CommonUtility.currencyFormat(
          (item.qty || 0) * (item.price || 0),
          currency,
        )}
      </AppTableCell>
    </AppTableRow>
  );
}

export function CustomLineItems({
  items,
  isEditMode,
  removeItem,
  loading,
  updateItem,
  currency,
}: ICustomLineItemsProps) {
  const columns: ITableColumn[] = useMemo(
    () => [
      {
        text: 'Item Name',
        key: 'item_name',
        textAlign: 'left',
      },
      {
        text: 'Quantity',
        key: 'qty',
        width: 2,
        textAlign: 'left',
      },
      {
        text: 'Unit Cost',
        key: 'price',
        width: 2,
        textAlign: 'left',
      },
      {
        text: 'Total',
        key: 'total',
        width: 3,
        textAlign: 'left',
      },
    ],
    [],
  );

  return (
    <div className="sk:my-4">
      <PageHeaderText>Additional Items</PageHeaderText>
      <AppTable
        data={items}
        loading={loading}
        keyField="item_name"
        columns={columns}
        RowComponent={
          <ItemRow
            isEditMode={isEditMode}
            removeItem={removeItem}
            updateItem={updateItem}
            currency={currency}
          />
        }
      />
    </div>
  );
}
